import { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDA, fmtDate, todayISO, STATUT_BCF } from "@/lib/format";
import { downloadPdf, openFile } from "@/lib/files";
import { FileUpload } from "@/components/FileUpload";
import { Plus, ShoppingCart, Loader2, Trash2, FileDown, PackageCheck, Paperclip } from "lucide-react";
import { toast } from "sonner";

const emptyLine = () => ({ produit_id: "", designation: "", reference: "", quantite: 1, prix_unitaire: 0, remise: 0 });

export default function Achats() {
  const { societes, societeParam, societeId } = useApp();
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [produits, setProduits] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [recFor, setRecFor] = useState(null);
  const [rec, setRec] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bons-commande-fournisseur", { params: societeParam });
      setItems(data.items);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [societeId]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const loadRefs = (sid) => {
    if (!sid) return;
    api.get("/fournisseurs", { params: { societe_id: sid } }).then((r) => setFournisseurs(r.data));
    api.get("/produits", { params: { societe_id: sid } }).then((r) => setProduits(r.data));
  };

  const openNew = () => {
    const sid = societeId !== "all" ? societeId : (societes[0]?.id || "");
    setForm({ societe_id: sid, fournisseur_id: "", date: todayISO(), tva_taux: 19, date_livraison_prevue: todayISO(), lieu_livraison: "", piece: null, lignes: [emptyLine()] });
    loadRefs(sid); setOpen(true);
  };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLine = (i, k, v) => setForm((f) => { const l = [...f.lignes]; l[i] = { ...l[i], [k]: v }; return { ...f, lignes: l }; });
  const pickProduct = (i, pid) => { const p = produits.find((x) => x.id === pid); setForm((f) => { const l = [...f.lignes]; l[i] = { ...l[i], produit_id: pid, designation: p?.nom || l[i].designation, reference: p?.reference || "", prix_unitaire: p?.prix_achat || l[i].prix_unitaire }; return { ...f, lignes: l }; }); };
  const addLine = () => setForm((f) => ({ ...f, lignes: [...f.lignes, emptyLine()] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lignes: f.lignes.filter((_, idx) => idx !== i) }));

  const ht = form ? form.lignes.reduce((s, l) => s + l.quantite * l.prix_unitaire * (1 - (l.remise || 0) / 100), 0) : 0;

  const submit = async () => {
    if (!form.societe_id || !form.fournisseur_id) return toast.error("Société et fournisseur requis.");
    if (form.lignes.some((l) => !l.designation)) return toast.error("Chaque ligne doit avoir une désignation.");
    setSaving(true);
    try {
      await api.post("/bons-commande-fournisseur", { ...form, tva_taux: Number(form.tva_taux) || 0, piece_id: form.piece?.id || null, piece_nom: form.piece?.filename || null, lignes: form.lignes.map((l) => ({ ...l, produit_id: l.produit_id || null, quantite: Number(l.quantite), prix_unitaire: Number(l.prix_unitaire), remise: Number(l.remise) || 0 })) });
      toast.success("Bon de commande créé."); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const openRec = (b) => { setRecFor(b); setRec({ date: todayISO(), observation: "", lignes: b.lignes.map((l, i) => ({ ligne_index: i, quantite_livree: Math.max(0, (l.quantite || 0) - (l.quantite_recue || 0)) })) }); };
  const submitRec = async () => {
    try {
      await api.post(`/bons-commande-fournisseur/${recFor.id}/reception`, { date: rec.date, observation: rec.observation, lignes: rec.lignes.filter((l) => l.quantite_livree > 0).map((l) => ({ ligne_index: l.ligne_index, quantite_livree: Number(l.quantite_livree) })) });
      toast.success("Réception enregistrée — stock mis à jour."); setRecFor(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Achats — Bons de commande" subtitle="Commandes fournisseurs et réceptions (entrée de stock automatique)" testid="achats-page"
        actions={can("achats") ? <Button onClick={openNew} className="gap-2" data-testid="new-bcf-btn"><Plus className="h-4 w-4" /> Nouveau bon</Button> : null} />

      <div className="rounded-xl border bg-card overflow-hidden">
        {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          : items.length === 0 ? <EmptyState icon={ShoppingCart} title="Aucun bon de commande" hint="Créez un bon de commande fournisseur, puis réceptionnez les marchandises pour alimenter le stock." />
          : (
            <div className="overflow-x-auto">
              <Table data-testid="bcf-table">
                <TableHeader><TableRow className="bg-muted/50"><TableHead>N°</TableHead><TableHead>Fournisseur</TableHead><TableHead>Date</TableHead><TableHead className="text-right">TTC</TableHead><TableHead>Statut</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {items.map((b) => {
                    const st = STATUT_BCF[b.statut] || {};
                    return (
                      <TableRow key={b.id} data-testid={`bcf-row-${b.id}`}>
                        <TableCell className="tabular text-sm font-medium">{b.numero}</TableCell>
                        <TableCell className="text-sm">{b.fournisseur_name}</TableCell>
                        <TableCell className="tabular text-sm">{fmtDate(b.date)}</TableCell>
                        <TableCell className="tabular text-right">{fmtDA(b.ttc)}</TableCell>
                        <TableCell><span className={`text-xs px-2 py-1 rounded-md whitespace-nowrap ${st.cls}`}>{st.label}</span></TableCell>
                        <TableCell>
                          <div className="flex gap-1 justify-end">
                            {b.piece_id && <Button size="icon" variant="ghost" className="h-8 w-8 text-primary" onClick={() => openFile(b.piece_id)} title={b.piece_nom || "Pièce jointe"} data-testid={`bcf-piece-${b.id}`}><Paperclip className="h-4 w-4" /></Button>}
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => downloadPdf(`/pdf/bcf/${b.id}`, b.numero)} data-testid={`bcf-pdf-${b.id}`}><FileDown className="h-4 w-4" /></Button>
                            {b.statut !== "recu" && b.statut !== "annule" && <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => openRec(b)} data-testid={`rec-btn-${b.id}`}><PackageCheck className="h-3.5 w-3.5" /> Réception</Button>}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
      </div>

      {/* Create BCF */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="bcf-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Nouveau bon de commande fournisseur</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Fournisseur *</Label>
                  <Select value={form.fournisseur_id} onValueChange={(v) => setField("fournisseur_id", v)}>
                    <SelectTrigger data-testid="bcf-fournisseur"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent className="bg-popover max-h-64">
                      {fournisseurs.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Aucun fournisseur</div>}
                      {fournisseurs.map((f) => <SelectItem key={f.id} value={f.id}>{f.raison_sociale}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Date</Label><DatePicker value={form.date} onChange={(v) => setField("date", v)} testid="bcf-date" /></div>
                <div className="space-y-1.5"><Label>Livraison prévue</Label><DatePicker value={form.date_livraison_prevue} onChange={(v) => setField("date_livraison_prevue", v)} testid="bcf-livraison" /></div>
              </div>
              <div className="rounded-lg border overflow-hidden">
                <div className="bg-muted/50 px-3 py-2 text-xs font-semibold grid grid-cols-12 gap-2"><span className="col-span-4">Désignation</span><span className="col-span-3">Produit (stock)</span><span className="col-span-1 text-right">Qté</span><span className="col-span-2 text-right">P.U.</span><span className="col-span-1 text-right">Rem.%</span><span className="col-span-1"></span></div>
                {form.lignes.map((l, i) => (
                  <div key={i} className="px-3 py-2 grid grid-cols-12 gap-2 items-center border-t">
                    <Input className="col-span-4 h-9" value={l.designation} onChange={(e) => setLine(i, "designation", e.target.value)} placeholder="Article" data-testid={`bcf-line-desig-${i}`} />
                    <div className="col-span-3">
                      <Select value={l.produit_id} onValueChange={(v) => pickProduct(i, v)}>
                        <SelectTrigger className="h-9"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent className="bg-popover max-h-56">{produits.map((p) => <SelectItem key={p.id} value={p.id}>{p.nom}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <Input className="col-span-1 h-9 text-right" type="number" value={l.quantite} onChange={(e) => setLine(i, "quantite", e.target.value)} data-testid={`bcf-line-qte-${i}`} />
                    <Input className="col-span-2 h-9 text-right" type="number" value={l.prix_unitaire} onChange={(e) => setLine(i, "prix_unitaire", e.target.value)} />
                    <Input className="col-span-1 h-9 text-right" type="number" value={l.remise} onChange={(e) => setLine(i, "remise", e.target.value)} />
                    <button className="col-span-1 text-muted-foreground hover:text-destructive flex justify-center" onClick={() => removeLine(i)} disabled={form.lignes.length === 1}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                <button onClick={addLine} className="w-full text-sm text-primary py-2 border-t hover:bg-accent/40 flex items-center justify-center gap-1.5" data-testid="bcf-add-line"><Plus className="h-4 w-4" /> Ajouter une ligne</button>
              </div>
              <div className="flex items-end justify-between">
                <div className="space-y-1.5 w-28"><Label>TVA (%)</Label><Input type="number" value={form.tva_taux} onChange={(e) => setField("tva_taux", e.target.value)} /></div>
                <div className="text-right tabular"><p className="text-sm text-muted-foreground">HT : <span className="text-foreground font-medium">{fmtDA(ht)}</span></p><p className="font-heading text-lg font-bold text-primary">TTC : {fmtDA(ht * (1 + (Number(form.tva_taux) || 0) / 100))}</p></div>
              </div>
              <div className="space-y-1.5"><Label>Bon de commande signé (PDF / photo)</Label><FileUpload value={form.piece} onChange={(v) => setField("piece", v)} testid="bcf-piece" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={saving} data-testid="bcf-submit" className="gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reception */}
      <Dialog open={!!recFor} onOpenChange={(v) => !v && setRecFor(null)}>
        <DialogContent className="bg-popover max-w-lg" data-testid="rec-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Réception — {recFor?.numero}</DialogTitle></DialogHeader>
          {recFor && rec && (
            <div className="space-y-4">
              <div className="w-40"><Label>Date de réception</Label><DatePicker value={rec.date} onChange={(v) => setRec((r) => ({ ...r, date: v }))} testid="rec-date" /></div>
              <div className="rounded-lg border overflow-hidden">
                <div className="bg-muted/50 px-3 py-2 text-xs font-semibold grid grid-cols-12 gap-2"><span className="col-span-6">Article</span><span className="col-span-2 text-right">Cmd</span><span className="col-span-2 text-right">Déjà</span><span className="col-span-2 text-right">Livré</span></div>
                {recFor.lignes.map((l, i) => (
                  <div key={i} className="px-3 py-2 grid grid-cols-12 gap-2 items-center border-t text-sm">
                    <span className="col-span-6 truncate">{l.designation}</span>
                    <span className="col-span-2 text-right tabular">{l.quantite}</span>
                    <span className="col-span-2 text-right tabular text-muted-foreground">{l.quantite_recue || 0}</span>
                    <Input className="col-span-2 h-8 text-right" type="number" value={rec.lignes[i].quantite_livree} onChange={(e) => setRec((r) => { const ls = [...r.lignes]; ls[i] = { ...ls[i], quantite_livree: e.target.value }; return { ...r, lignes: ls }; })} data-testid={`rec-line-${i}`} />
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">La quantité livrée est ajoutée automatiquement au stock des produits liés. Le reliquat est conservé.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecFor(null)}>Annuler</Button>
            <Button onClick={submitRec} data-testid="rec-submit">Valider la réception</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
